/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { createBrowserRouter, Link, Navigate, useParams } from "react-router-dom";
import AppShell from "./ui/AppShell";

// Guard
import RequireAuth from "./components/RequireAuth";
import NotFound from "./pages/NotFound";

/** -------------------------
 * Public / Auth (no shell)
 * ------------------------*/
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import RegisterPersonal from "./pages/RegisterPersonal";
import RegisterBusiness from "./pages/RegisterBusiness";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import EmailVerifyPrompt from "./pages/EmailVerifyPrompt";
import EmailVerified from "./pages/EmailVerified";

/** -------------------------
 * Core (inside AppShell)
 * ------------------------*/
import Dashboard from "./pages/Dashboard";

// Accounts
import AccountsIndex from "./pages/AccountsIndex";
import AccountsRequest from "./pages/AccountsRequest";
import AccountsChangePlan from "./pages/AccountsChangePlan";

// Transfers
import TransfersIndex from "./pages/TransfersIndex";
import TransferCreate from "./pages/TransferCreate";
import TransferCreateDetails from "./pages/TransferCreateDetails";
import TransferDetails from "./pages/TransferDetails";

// Funding
import FundingIndex from "./pages/FundingIndex";
import FundingCreate from "./pages/FundingCreate";
import FundingShow from "./pages/FundingShow";

// Compliance (Funding docs)
import ComplianceFundingDocuments from "./pages/ComplianceFundingDocuments";
import ComplianceFundingUpload from "./pages/ComplianceFundingUpload";

// Transactions
import TransactionsIndex from "./pages/TransactionsIndex";

// Recipients
import RecipientsIndex from "./pages/RecipientsIndex";
import RecipientCreate from "./pages/RecipientCreate";
import RecipientEdit from "./pages/RecipientEdit";
import RecipientShow from "./pages/RecipientShow";

// Deposits
import DepositsShow from "./pages/DepositsShow";

// FX
import FxCreate from "./pages/FxCreate";
import FxConfirm from "./pages/FxConfirm";

// Trust pages
import TrustsIndex from "./pages/TrustsIndex";
import TrustDashboard from "./pages/TrustDashboard";
import TrustsShow from "./pages/TrustsShow";
import TrustApply from "./pages/TrustApply";
import TrustTransfer from "./pages/TrustTransfer";
import TrustWishCreate from "./pages/TrustWishCreate";
import TrustWishVerify from "./pages/TrustWishVerify";
import TrustsWishShow from "./pages/TrustsWishShow";

// KYC
import KycStatus from "./pages/KycStatus";
import KycCreate from "./pages/KycCreate";
import KycEdit from "./pages/KycEdit";

// KYB
import KybCreate from "./pages/KybCreate";
import KybStatus from "./pages/KybStatus";
import KybEdit from "./pages/KybEdit";

// Profile
import ProfileOverview from "./pages/ProfileOverview";
import ProfileSecurity from "./pages/ProfileSecurity";
import ProfileEmailChange from "./pages/ProfileEmailChange";
import ProfileTwoFactor from "./pages/ProfileTwoFactor";
import ProfileEdit from "./pages/ProfileEdit";
import ProfileUsers from "./pages/ProfileUsers";
import PhoneVerify from "./pages/PhoneVerify";

// UBOs
import UboIndex from "./pages/UboIndex";
import UboCreate from "./pages/UboCreate";
import UboEdit from "./pages/UboEdit";
import UboUpload from "./pages/UboUpload";

function TrustWishRedirect({ verify = false }: { verify?: boolean }) {
  const { id, wishId } = useParams<{ id?: string; wishId?: string }>();
  const resolved = id ?? wishId;

  if (!resolved) return <Navigate to="/trusts" replace />;

  return <Navigate to={`/trusts/wishes/${resolved}${verify ? "/verify" : ""}`} replace />;
}

function ModuleGate({
  module,
  children,
}: {
  module: string;
  children: React.ReactElement;
}) {
  const [state, setState] = React.useState<"loading" | "enabled" | "disabled">("loading");

  React.useEffect(() => {
    let alive = true;

    fetch("/xapi/me", { credentials: "include", headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!alive) return;
        const enabled = json?.modules?.[module]?.enabled;
        setState(enabled === false ? "disabled" : "enabled");
      })
      .catch(() => {
        if (alive) setState("enabled");
      });

    return () => {
      alive = false;
    };
  }, [module]);

  if (state === "loading") {
    return <div className="section-box skeleton">Loading...</div>;
  }

  if (state === "disabled") {
    return (
      <div className="section-box max-w-xl mx-auto p-6">
        <h1 className="text-xl font-semibold">Module unavailable</h1>
        <p className="mt-2 text-gray-600">
          This feature is disabled for the current tenant configuration.
        </p>
        <div className="mt-4">
          <Link to="/dashboard" className="btn">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return children;
}

function gated(module: string, element: React.ReactElement) {
  return <ModuleGate module={module}>{element}</ModuleGate>;
}

const router = createBrowserRouter(
  [
    // ---------- Public landing + auth ----------
    { path: "/", element: <Navigate to="/landing" replace /> },
    { path: "/landing", element: <Landing /> },
    { path: "/login", element: <Login /> },

    // Split registration flows (country dropdown powered by /xapi/countries)
    { path: "/register/personal", element: <RegisterPersonal /> },
    { path: "/register/business", element: <RegisterBusiness /> },
    // Back-compat: legacy /register -> /register/personal
    { path: "/register", element: <Navigate to="/register/personal" replace /> },

    { path: "/forgot-password", element: <ForgotPassword /> },
    { path: "/reset-password", element: <ResetPassword /> },
    { path: "/verify-email", element: <VerifyEmail /> },
    { path: "/email/verify/prompt", element: <EmailVerifyPrompt /> },
    { path: "/email/verified", element: <EmailVerified /> },

    // ---------- Protected app shell ----------
    {
      path: "/",
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: "dashboard", element: <Dashboard /> },

        // Accounts
        { path: "accounts", element: gated("accounts", <AccountsIndex />) },
        { path: "accounts/request", element: gated("accounts", <AccountsRequest />) },
        { path: "accounts/:id/change-plan", element: gated("accounts", <AccountsChangePlan />) },

        // Transfers
        { path: "transfers", element: gated("banking_rails", <TransfersIndex />) },
        { path: "transfers/create", element: gated("banking_rails", <TransferCreate />) },

        // alias for old Blade-style URL with dash
        { path: "transfers/create-details", element: gated("banking_rails", <TransferCreateDetails />) },

        // existing slash route (keep if you want)
        { path: "transfers/create/details", element: gated("banking_rails", <TransferCreateDetails />) },

        // NEW: support both /app/transfers/8 and /app/transfers/8/details
        { path: "transfers/:id", element: gated("banking_rails", <TransferDetails />) },
        { path: "transfers/:id/details", element: gated("banking_rails", <TransferDetails />) },

        // Funding
        { path: "funding", element: gated("banking_rails", <FundingIndex />) },
        { path: "funding/create", element: gated("banking_rails", <FundingCreate />) },
        { path: "funding/:id", element: gated("banking_rails", <FundingShow />) },

        // Compliance (Funding docs)
        {
          path: "compliance/funding/:id/documents",
          element: gated("compliance", <ComplianceFundingDocuments />),
        },
        {
          path: "compliance/funding/:id/upload",
          element: gated("compliance", <ComplianceFundingUpload />),
        },

        // Transactions
        { path: "transactions", element: <TransactionsIndex /> },

        // Recipients
        { path: "recipients", element: <RecipientsIndex /> },
        { path: "recipients/create", element: <RecipientCreate /> },
        { path: "recipients/:id", element: <RecipientShow /> },
        { path: "recipients/:id/edit", element: <RecipientEdit /> },

        // Deposits
        { path: "deposits/:account", element: <DepositsShow /> },

        // FX
        { path: "fx/create", element: gated("fx", <FxCreate />) },
        { path: "fx/confirm", element: gated("fx", <FxConfirm />) },

        // ---- Trust (singular = global dashboard/flows) ----
        { path: "trust", element: gated("trust", <TrustDashboard />) },
        { path: "trust/apply", element: gated("trust", <TrustApply />) },
        { path: "trust/transfer", element: gated("trust", <TrustTransfer />) },

        // ---- Trusts (plural = collection/item + wishes) ----
        { path: "trusts", element: gated("trust", <TrustsIndex />) },
        { path: "trusts/:id", element: gated("trust", <TrustsShow />) },
        // NEW: reapply route (SPA side) – fixes /app/trusts/:id/reapply 404
        { path: "trusts/:id/reapply", element: gated("trust", <TrustApply mode="reapply" />) },
        { path: "trusts/:id/wishes/create", element: gated("trust", <TrustWishCreate />) },
        { path: "trusts/wishes/:id", element: gated("trust", <TrustsWishShow />) },
        { path: "trusts/wishes/:id/verify", element: gated("trust", <TrustWishVerify />) },

        // ---- Back-compat redirects for old singular wish URLs ----
        { path: "trust/wishes/:id", element: <TrustWishRedirect /> },
        {
          path: "trust/wishes/:wishId/verify",
          element: <TrustWishRedirect verify />,
        },

        // KYC
        { path: "kyc", element: gated("kyc", <KycStatus />) },
        { path: "kyc/create", element: gated("kyc", <KycCreate />) },
        { path: "kyc/edit", element: gated("kyc", <KycEdit />) },
        { path: "kyc/status", element: <Navigate to="/kyc" replace /> },

        // KYB
        { path: "kyb", element: gated("kyb", <KybStatus />) },          // <- status is canonical
        { path: "kyb/create", element: gated("kyb", <KybCreate />) },   // <- first submission form
        { path: "kyb/:id/edit", element: gated("kyb", <KybEdit />) },   // <- param name matches useParams<{ id }>
        { path: "kyb/status", element: <Navigate to="/kyb" replace /> },

        // Profile
        { path: "profile", element: <ProfileOverview /> },
        { path: "profile/edit", element: <ProfileEdit /> },
        { path: "profile/security", element: <ProfileSecurity /> },
        { path: "profile/2fa", element: <ProfileTwoFactor /> },
        { path: "profile/email/change", element: <ProfileEmailChange /> },
        { path: "profile/users", element: <ProfileUsers /> },
        { path: "phone/verify", element: <PhoneVerify /> },

        // UBOs
        { path: "profile/ubos", element: gated("ubo", <UboIndex />) },
        { path: "profile/ubos/create", element: gated("ubo", <UboCreate />) },
        { path: "profile/ubos/:id/edit", element: gated("ubo", <UboEdit />) },
        { path: "profile/ubos/:id/upload", element: gated("ubo", <UboUpload />) },

        // In-app 404
        { path: "*", element: <NotFound /> },
      ],
    },

    // Top-level 404 -> landing
    { path: "*", element: <Navigate to="/landing" replace /> },
  ],
  {
    basename: "/app",
  }
);

export default router;
